import { FieldDefinition, PanelField } from '../types/highlights'

const currency = (value: number): string =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  })

const empty = '—'

export const fieldCatalog: FieldDefinition[] = [
  {
    id: 'loanPurpose',
    label: 'Loan Purpose',
    category: 'Loan',
    type: 'text',
    getRaw: (l) => l.loanPurpose,
    getValue: (l) => l.loanPurpose,
  },
  {
    id: 'chosenLender',
    label: 'Chosen Lender',
    category: 'Loan',
    type: 'text',
    getRaw: (l) => l.chosenLender,
    getValue: (l) => l.chosenLender,
  },
  {
    id: 'currentLoanStatus',
    label: 'Current Loan Status',
    category: 'Milestones',
    type: 'text',
    getRaw: (l) => l.currentLoanStatus,
    getValue: (l) => l.currentLoanStatus,
  },
  {
    id: 'qmType',
    label: 'QM/Non-QM',
    category: 'Compliance',
    type: 'text',
    getRaw: (l) => l.qmType,
    getValue: (l) => l.qmType,
  },
  {
    id: 'madisonEcd',
    label: 'Madison ECD',
    category: 'Milestones',
    type: 'date',
    getRaw: (l) => l.madisonEcd,
    getValue: (l) => l.madisonEcd,
  },
  {
    id: 'rateLockExp',
    label: 'Rate Lock Exp',
    category: 'Pricing',
    type: 'date',
    getRaw: (l) => l.rateLockExp,
    getValue: (l) => l.rateLockExp ?? empty,
  },
  {
    id: 'loanAmount',
    label: 'Loan Amount',
    category: 'Loan',
    type: 'number',
    getRaw: (l) => l.loanAmount,
    getValue: (l) => currency(l.loanAmount),
  },
  {
    id: 'rate',
    label: 'Rate',
    category: 'Pricing',
    type: 'number',
    getRaw: (l) => l.rate,
    getValue: (l) => `${l.rate.toFixed(3)}%`,
  },
  {
    id: 'dti',
    label: 'DTI',
    category: 'Risk',
    type: 'number',
    getRaw: (l) => l.dti,
    getValue: (l) => (l.dti === null ? empty : `${l.dti.toFixed(1)}%`),
  },
  {
    id: 'ltv',
    label: 'LTV/CLTV',
    category: 'Risk',
    type: 'number',
    getRaw: (l) => l.ltv,
    getValue: (l) => `${l.ltv.toFixed(1)}/${l.cltv.toFixed(1)}`,
  },
  {
    id: 'cltv',
    label: 'Combined LTV',
    category: 'Risk',
    type: 'number',
    getRaw: (l) => l.cltv,
    getValue: (l) => `${l.cltv.toFixed(1)}%`,
  },
  {
    id: 'fico',
    label: 'FICO',
    category: 'Risk',
    type: 'number',
    getRaw: (l) => l.fico,
    getValue: (l) => String(l.fico),
  },
  {
    id: 'loanProgram',
    label: 'Loan Program',
    category: 'Loan',
    type: 'text',
    getRaw: (l) => l.loanProgram,
    getValue: (l) => l.loanProgram ?? empty,
  },
  {
    id: 'subjectPropertyAddress',
    label: 'Subject Property Address',
    category: 'Property',
    type: 'text',
    getRaw: (l) => l.subjectPropertyAddress,
    getValue: (l) => l.subjectPropertyAddress,
  },
  {
    id: 'appraisalStatus',
    label: 'Appraisal Status',
    category: 'Property',
    type: 'text',
    getRaw: (l) => l.appraisalStatus,
    getValue: (l) => l.appraisalStatus,
  },
  {
    id: 'appraisedValue',
    label: 'Appraised Value',
    category: 'Property',
    type: 'number',
    getRaw: (l) => l.appraisedValue,
    getValue: (l) => currency(l.appraisedValue),
  },
  {
    id: 'closingDate',
    label: 'Closing Date',
    category: 'Milestones',
    type: 'date',
    getRaw: (l) => l.closingDate,
    getValue: (l) => l.closingDate,
  },
  {
    id: 'conditionsOutstanding',
    label: 'Open Conditions',
    category: 'Milestones',
    type: 'number',
    getRaw: (l) => l.conditionsOutstanding,
    getValue: (l) => String(l.conditionsOutstanding),
  },
  {
    id: 'docsExpiringDays',
    label: 'Docs Expiring',
    category: 'Compliance',
    type: 'number',
    getRaw: (l) => l.docsExpiringDays,
    getValue: (l) => (l.docsExpiringDays === null ? empty : `${l.docsExpiringDays} days`),
  },
  {
    id: 'reservesMonths',
    label: 'Reserves',
    category: 'Risk',
    type: 'number',
    getRaw: (l) => l.reservesMonths,
    getValue: (l) => `${l.reservesMonths.toFixed(1)} mo`,
  },
  {
    id: 'cashToClose',
    label: 'Cash to Close',
    category: 'Loan',
    type: 'number',
    getRaw: (l) => l.cashToClose,
    getValue: (l) => currency(l.cashToClose),
  },
  {
    id: 'monthlyPayment',
    label: 'Monthly Payment',
    category: 'Loan',
    type: 'number',
    getRaw: (l) => l.monthlyPayment,
    getValue: (l) => currency(l.monthlyPayment),
  },
  {
    id: 'escrowWaived',
    label: 'Escrow Waived',
    category: 'Compliance',
    type: 'boolean',
    getRaw: (l) => l.escrowWaived,
    getValue: (l) => (l.escrowWaived ? 'Yes' : 'No'),
  },
  {
    id: 'occupancy',
    label: 'Occupancy',
    category: 'Property',
    type: 'text',
    getRaw: (l) => l.occupancy,
    getValue: (l) => l.occupancy,
  },
  {
    id: 'propertyType',
    label: 'Property Type',
    category: 'Property',
    type: 'text',
    getRaw: (l) => l.propertyType,
    getValue: (l) => l.propertyType,
  },
  {
    id: 'borrower',
    label: 'Borrower',
    category: 'People',
    type: 'text',
    getRaw: (l) => l.borrower,
    getValue: (l) => l.borrower,
  },
  {
    id: 'loanOfficer',
    label: 'Loan Officer',
    category: 'People',
    type: 'text',
    getRaw: (l) => l.loanOfficer,
    getValue: (l) => l.loanOfficer,
  },
  {
    id: 'processor',
    label: 'Processor',
    category: 'People',
    type: 'text',
    getRaw: (l) => l.processor,
    getValue: (l) => l.processor,
  },
]

export const defaultPanelFields: PanelField[] = [
  { fieldId: 'loanPurpose' },
  { fieldId: 'chosenLender' },
  { fieldId: 'currentLoanStatus' },
  { fieldId: 'qmType' },
  { fieldId: 'madisonEcd' },
  { fieldId: 'rateLockExp' },
  { fieldId: 'loanAmount' },
  { fieldId: 'rate' },
  { fieldId: 'dti' },
  { fieldId: 'ltv' },
  { fieldId: 'fico' },
  { fieldId: 'loanProgram' },
  { fieldId: 'subjectPropertyAddress' },
]
